import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Switch, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ApprovalWorkflowService } from '../../services/approval.service';
import {
  APPROVER_TYPE_LABEL, ApprovalStepDTO, ApprovalSubjectType, ApprovalWorkflow, ApproverOptions,
} from '../../interfaces/approval.interface';
import { OPERATOR_LABEL } from '../../interfaces/bank/bankRule.interface';
import ApprovalWorkflowEditorDialog from './ApprovalWorkflowEditorDialog';

const stepText = (s: ApprovalStepDTO): string => {
  const who = s.approverType === 'USER' ? s.approverUserName
    : s.approverType === 'ROLE' ? s.approverRoleName
    : s.approverType === 'DEPARTMENT_ADMIN' ? `${s.departmentName ?? 'department'} admin`
    : APPROVER_TYPE_LABEL[s.approverType];
  return `${s.seq}. ${who ?? '—'}${s.authorityLimit != null ? ` (up to ${s.authorityLimit})` : ''}`;
};

/** Settings > Approval workflows: per document type, the ordered workflows that decide who must approve. */
const ApprovalWorkflows: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [types, setTypes] = useState<ApprovalSubjectType[]>([]);
  const [current, setCurrent] = useState('');
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [options, setOptions] = useState<ApproverOptions>({ members: [], roles: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<{ workflow: ApprovalWorkflow | null } | null>(null);

  useEffect(() => {
    Promise.resolve(ApprovalWorkflowService.subjectTypes()).then(t => {
      setTypes(t ?? []);
      if (t?.length) setCurrent(c => c || t[0].subjectType);
      else setLoading(false);
    }).catch(e => { toast.showApiError(e, 'Failed to load document types'); setLoading(false); });
    Promise.resolve(ApprovalWorkflowService.options(institutionId)).then(o => setOptions(o)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId]);

  const load = useCallback(async () => {
    if (!current) return;
    try {
      setLoading(true);
      setWorkflows(await ApprovalWorkflowService.list(institutionId, current));
    } catch (e) {
      toast.showApiError(e, 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, current]);

  useEffect(() => { load(); }, [load]);

  const subject = types.find(t => t.subjectType === current);

  const move = async (index: number, delta: number) => {
    const next = [...workflows];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      setWorkflows(await ApprovalWorkflowService.reorder(institutionId, current, next.map(w => w.rule.id)));
    } catch (e) {
      toast.showApiError(e, 'Failed to reorder workflows');
    }
  };
  const toggle = async (w: ApprovalWorkflow) => {
    try { await ApprovalWorkflowService.setActive(institutionId, w.rule.id, !w.rule.active); await load(); }
    catch (e) { toast.showApiError(e, 'Failed to change the workflow status'); }
  };
  const remove = async (w: ApprovalWorkflow) => {
    if (!window.confirm(`Delete workflow “${w.rule.name}”?`)) return;
    try { await ApprovalWorkflowService.remove(institutionId, w.rule.id); toast.showSuccess('Workflow deleted'); await load(); }
    catch (e) { toast.showApiError(e, 'Failed to delete the workflow'); }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="approvals.workflows.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Approval workflows</Typography>
          <Typography variant="body2" color="text.secondary">
            Workflows are tried top to bottom; the first match decides who approves. A document no workflow matches needs no approval.
          </Typography>
        </Box>
        {subject && <Button data-help="approvals.workflows.new" variant="contained" startIcon={<AddIcon />} onClick={() => setEditor({ workflow: null })}>New workflow</Button>}
      </Box>
      {types.length === 0 && !loading && <Alert severity="info">No document type uses approvals yet.</Alert>}
      {types.length > 0 && (
        <Tabs data-help-section="approvals.workflows.tabs" value={current} onChange={(_, v) => setCurrent(v)} sx={{ mb: 2 }}>
          {types.map(t => <Tab key={t.subjectType} value={t.subjectType} label={t.label} />)}
        </Tabs>
      )}
      {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : subject && (
        <Paper variant="outlined" data-help-section="approvals.workflows.table">
          <TableContainer>
            <Table size="small" data-testid="approval-workflows-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="approvals.workflows.col.order">#</TableCell><TableCell data-help="approvals.workflows.col.name">Workflow</TableCell><TableCell data-help="approvals.workflows.col.when">When</TableCell><TableCell data-help="approvals.workflows.col.steps">Steps</TableCell>
                  <TableCell data-help="approvals.workflows.col.active">Active</TableCell><TableCell align="right" data-help="approvals.workflows.col.actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((w, i) => (
                  <TableRow key={w.rule.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{w.rule.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 200 }}>
                      {w.rule.conditions.length === 0 ? <Typography variant="caption">Every document</Typography>
                        : w.rule.conditions.map((c, idx) => (
                          <Chip key={idx} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }}
                            label={`${c.field} ${OPERATOR_LABEL[c.operator]} ${c.valueNumber ?? c.valueText ?? ''}`} />
                        ))}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>{w.steps.map(s => <Typography key={s.seq} variant="body2">{stepText(s)}</Typography>)}</TableCell>
                    <TableCell>
                      <Switch data-help="approvals.workflows.action.toggle" size="small" checked={w.rule.active} onChange={() => toggle(w)}
                        inputProps={{ 'aria-label': `${w.rule.active ? 'Deactivate' : 'Activate'} ${w.rule.name}` }} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Move up"><span data-help="approvals.workflows.action.moveUp"><IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${w.rule.name} up`}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Move down"><span data-help="approvals.workflows.action.moveDown"><IconButton size="small" disabled={i === workflows.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${w.rule.name} down`}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <IconButton data-help="approvals.workflows.action.edit" size="small" onClick={() => setEditor({ workflow: w })} aria-label={`Edit ${w.rule.name}`}><EditIcon fontSize="small" /></IconButton>
                      <IconButton data-help="approvals.workflows.action.delete" size="small" onClick={() => remove(w)} aria-label={`Delete ${w.rule.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {workflows.length === 0 && <TableRow><TableCell colSpan={6}>No workflow yet: documents of this type need no approval.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {editor && subject && (
        <ApprovalWorkflowEditorDialog subject={subject} workflow={editor.workflow} options={options}
          referenceOptions={{}}
          onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </Box>
  );
};

export default ApprovalWorkflows;
